/**
 * Code-level switches of the sales-experience changes (docs/ux-and-autonomy.md).
 *
 * Every change here only changes what a person sees or how many clicks a step takes; a
 * person still decides. They ship ON. Setting one to `false` and rebuilding takes the
 * change out again without touching the rest.
 *
 * Nothing here widens what I2Q accepts, closes or decides on its own: those switches are
 * rows in the app database (`autonomy_*`), OFF by default, and are read by the server.
 *
 * What a viewer can turn off for themselves lives in the URL (queue toggles) or in this
 * browser (`shortcutsOn`), guarded because storage can be unavailable.
 */

export const FEATURES = {
  /** One strip under the case header: customer, lines, next step, reply draft (U2). */
  summaryStrip: true,
  /** "Accept N matched items" and "Confirm" on a matched line (U1). */
  acceptMatched: true,
  /** Next and previous case, "Mark done and next", a sticky work bar (U3). */
  caseFlow: true,
  /** Keyboard shortcuts on the queue and the case page (U3). */
  shortcuts: true,
  /** Select rows in the queue and set their status in one go (U4). */
  bulkActions: true,
  /** Replies without lines of their own folded under their first mail (A3). */
  groupThreads: true,
  /** Inquiries forwarded by a colleague shown by default and labelled (U6). */
  colleagueForwards: true,
  /** Backlog chips, "Older than 7 days" and the open-for badge (U5). */
  backlogChips: true,
  /** "Copy reply" and "Copy lines" (U8). */
  copyReady: true,
  /** "Back for you" reminders inside the app (A4). Nothing is sent. */
  backForYou: true,
  /**
   * "Usually handled by ..." on the queue row and the case page (Timo, 10 Oct 2026). Shown
   * only while the server switch `autonomy_suggest_owner` is ON. A suggestion only.
   */
  ownerSuggestion: true,
  /**
   * The sales training (11 Oct 2026): the Training menu entry and pages, the help topic,
   * "Practise this in the training" links and "Restart training" in the account menu.
   * Practice cases are answered in the browser; nothing is sent.
   */
  training: true,
  /** The first-login note on the queue ("New to I2Q? ..."), dismissible. */
  trainingNudge: true,
} as const;

export type FeatureName = keyof typeof FEATURES;

const SHORTCUTS_KEY = "i2q.shortcuts.off";

/** True unless this viewer turned the keyboard shortcuts off in this browser. */
export function shortcutsOn(): boolean {
  if (!FEATURES.shortcuts) {
    return false;
  }
  try {
    return window.localStorage.getItem(SHORTCUTS_KEY) !== "1";
  } catch {
    return true;
  }
}

/** Turn the keyboard shortcuts on or off for this browser. Fails silently without storage. */
export function setShortcutsOn(on: boolean): void {
  try {
    if (on) {
      window.localStorage.removeItem(SHORTCUTS_KEY);
    } else {
      window.localStorage.setItem(SHORTCUTS_KEY, "1");
    }
  } catch {
    // No storage in this browser: the choice lasts for this page only.
  }
}
